import { Inject, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { QformCard, QformFilePickerInput } from 'qflow-form-renderer';
import { firstValueFrom } from 'rxjs';
import {
  API_BASE_URL,
  AttachmentDataInputDto,
  AttachmentDataOutputDtoEntityActionResultDto,
  AttachmentsClient,
  FileParameter,
} from '../../core/remote-clients/remote-client';
import { RedirectService } from '../../core/services/redirect.service';
import { TennantResolverService } from '../../core/services/tennant-resolver.service';

@Injectable()
export class AttachmentService {
  private readonly isGuest: boolean = false;
  private readonly guestToken: string = '';
  private readonly tenantId: string = '';
  private readonly flowId: string = '';
  private readonly taskId: string = '';
  private readonly taskToId: string = '';
  private readonly email: string = '';

  attachments?: AttachmentDataInputDto[] = [];
  baseUrl: string;

  constructor(
    private readonly _attachmentClient: AttachmentsClient,
    activatedRoute: ActivatedRoute,
    private readonly _redirectService: RedirectService,
    private readonly _tenantResolver: TennantResolverService,
    @Inject(API_BASE_URL) baseUrl: string
  ) {
    this.baseUrl = baseUrl ?? '/Qflow.Task.Angular.Backend';
    this.isGuest = activatedRoute.snapshot.data['isGuest'];
    this.guestToken = activatedRoute.snapshot.queryParamMap.get('token') ?? '';
    this.tenantId = this._tenantResolver.resolve(activatedRoute);
    this.flowId = activatedRoute.snapshot.queryParamMap.get('flowId') ?? '';
    this.taskId = activatedRoute.snapshot.queryParamMap.get('taskId') ?? '';
    this.taskToId = activatedRoute.snapshot.queryParamMap.get('taskToId') ?? '';
    this.email = activatedRoute.snapshot.queryParamMap.get('email') ?? '';
  }

  async uploadTempFiles(
    qformCard: QformCard,
    templateVersionId: string
  ): Promise<AttachmentDataInputDto[] | undefined> {
    this.attachments = [];
    const inputs = qformCard.getInputs();
    await Promise.all(
      inputs.map(async input => {
        if (
          this.hasFileContent(input) &&
          input instanceof QformFilePickerInput
        ) {
          await this.uploadTempFileAndSetValues(
            input.fileContent!,
            input,
            templateVersionId
          );
        }
      })
    );
    return this.attachments;
  }


  async downloadFlowAttachment(
    attachId: string,
    version: number
  ): Promise<any> {
    this._redirectService.redirectToDownloadFlowAttachment(
      this.flowId,
      this.taskId,
      this.taskToId,
      attachId,
      version,
      this.tenantId,
      this.guestToken,
      this.email
    );
  }

  private hasFileContent(input: any): boolean {
    return 'fileContent' in input && !!input.fileContent;
  }

  private setInputValueFromResponse(input: any, response: any): void {
    input.value = `${response.entity.attachId};${response.entity.attachVersion};${response.entity.name}`;
  }

  private async uploadTempFileAndSetValues(
    file: File,
    input: QformFilePickerInput,
    templateVersionId: string
  ): Promise<void> {
    const fileParameter = this.createFileParameter(file);
    const response = await this.requestToUploadTempFile(
      fileParameter,
      input.value,
      templateVersionId
    );
    if (response?.success) {
      this.setInputValueFromResponse(input, response);
      const attachmentDto = this.createAttachmentDtoFromResponse(
        input.qflowId,
        response
      );

      this.attachments?.push(attachmentDto);
    }
  }

  private createAttachmentDtoFromResponse(
    refDataId: string,
    response: AttachmentDataOutputDtoEntityActionResultDto | undefined
  ): AttachmentDataInputDto {
    const attachmentDto = new AttachmentDataInputDto();
    attachmentDto.name = response?.entity?.name;
    attachmentDto.attachId = response?.entity?.attachId;
    attachmentDto.attachVersion = response?.entity?.attachVersion;
    attachmentDto.refDataId = refDataId;
    attachmentDto.size = response?.entity?.size;

    return attachmentDto;
  }

  private requestToUploadTempFile(
    fileParameter: FileParameter,
    inputValue: string,
    templateVersionId: string
  ) {
    const request = this.isGuest
      ? this._attachmentClient.guestPOST(
        inputValue,
        templateVersionId,
        fileParameter
      )
      : this._attachmentClient.attachmentsPOST(
        inputValue,
        templateVersionId,
        fileParameter
      );

    return firstValueFrom(request);
  }

  private createFileParameter(file: File): FileParameter {
    return {
      data: file,
      fileName: file.name,
    };
  }

  deleteFlowAttachment(
    flowId: string,
    taskId: string,
    taskToId: string,
    attachId: string,
    version: number
  ): Promise<any> {
    return firstValueFrom(
      this._attachmentClient.attachmentsGET(
        flowId,
        taskId,
        taskToId,
        attachId,
        version
      )
    );
  }
}
